/* eslint-disable eqeqeq */
import React, { useCallback, useEffect, useState } from "react";
import { useTable, usePagination, useSortBy, useFilters } from "react-table";
import _ from "lodash";

import TableUI from "../utils/ContainerTableInfiniteUI";
import CampaignTableUI from "../utils/ContainerTableInfiniteUI/CampaignTable";
import PhoneListTableUI from "../utils/ContainerTableInfiniteUI/PhoneListTable";
import AudioListTableUI from "../utils/ContainerTableInfiniteUI/AudioListTable";
import PaymentHistoryTableUI from "../utils/ContainerTableInfiniteUI/PaymentHistoryTableUI";
import AdminTableUI from "../utils/ContainerTableInfiniteUI/AdminTableUI";
import SubuserTableUI from "../utils/ContainerTableInfiniteUI/SubuserTableUI";

export default React.memo(
  ({
    data,
    columns,
    Filters,
    total,
    onFetchData,
    defaultPageSize = 8,
    minRows = 6,
    disableSorting = false,
    defaultSortBy = [],
    getRowProps,
    isLoading,
    hiddenIsLoading,
    intervalCall = false,
    tableName = "",
    ...rest
  }) => {
    const [filters, setFilterDefault] = useState({});
    const [controlledPageCount, setControlledPageCount] = useState(
      Math.ceil(total / (Math.max(defaultPageSize, minRows) + 2))
    );

    const {
      getTableProps,
      headerGroups,
      page,
      prepareRow,
      gotoPage,
      canPreviousPage,
      previousPage,
      canNextPage,
      nextPage,
      setPageSize,
      state: { pageIndex, sortBy, pageSize },
    } = useTable(
      {
        data,
        columns: React.useMemo(() => columns, [columns]),
        disableSorting,
        initialState: {
          pageSize: Math.max(defaultPageSize, minRows) + 2,
          sortBy: defaultSortBy,
        },
        manualFilters: true,
        manualPagination: true,
        manualSortBy: true,
        pageCount: controlledPageCount,
      },
      useFilters,
      useSortBy,
      usePagination
    );

    useEffect(() => {
      setControlledPageCount(Math.ceil(total / pageSize));
    }, [total, pageSize]);

    const setControlledPageSizeFn = useCallback(
      (size) => {
        setPageSize(size);
        setControlledPageCount(Math.ceil(total / size));
      },
      [total, setPageSize]
    );

    const setFilter = (column, value) => {
      const newFilter = {};
      newFilter[column] = value;
      if (pageIndex > 0) {
        gotoPage(0);
      }
      setFilterDefault({ ...filters, ...newFilter });
    };

    const setMultipleFilters = (newFilters) => {
      if (pageIndex > 0) {
        gotoPage(0);
      }
      setFilterDefault({ ...filters, ...newFilters });
    };

    React.useEffect(() => {
      let interval = null;
      const apiParams = {
        offset: 0,
        limit: (pageIndex + 1) * pageSize,
      };
      if (sortBy && sortBy.length)
        apiParams[`sortby[${sortBy[0].id}]`] = sortBy[0].desc ? "DESC" : "ASC";
      if (filters)
        _.map(filters, (filter, key) => {
          apiParams[`filters[${key}]`] = filter;
        });
      onFetchData(apiParams);

      if (intervalCall) {
        interval = setInterval(() => {
          onFetchData(apiParams);
        }, 5000);
      }
      return () => {
        if (intervalCall) clearInterval(interval);
      };
    }, [intervalCall, onFetchData, pageIndex, pageSize, sortBy, filters]);

    if (tableName == "campaigns") {
      return (
        <CampaignTableUI
          {...rest}
          getTableProps={getTableProps}
          getRowProps={getRowProps}
          headerGroups={headerGroups}
          page={page}
          isLoading={isLoading}
          hiddenIsLoading={hiddenIsLoading}
          prepareRow={prepareRow}
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          previousPage={previousPage}
          nextPage={nextPage}
          pageIndex={pageIndex}
          pageCount={controlledPageCount}
          setPageSize={setControlledPageSizeFn}
          pageSize={pageSize}
          filters={filters}
          setFilter={setFilter}
          setMultipleFilters={setMultipleFilters}
          CustomFilters={Filters}
          minRows={minRows}
          columns={columns}
        />
      );
    }

    if (tableName == "phonesList") {
      return (
        <PhoneListTableUI
          {...rest}
          getTableProps={getTableProps}
          getRowProps={getRowProps}
          headerGroups={headerGroups}
          page={page}
          isLoading={isLoading}
          hiddenIsLoading={hiddenIsLoading}
          prepareRow={prepareRow}
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          previousPage={previousPage}
          nextPage={nextPage}
          pageIndex={pageIndex}
          pageCount={controlledPageCount}
          setPageSize={setControlledPageSizeFn}
          pageSize={pageSize}
          filters={filters}
          setFilter={setFilter}
          setMultipleFilters={setMultipleFilters}
          CustomFilters={Filters}
          minRows={minRows}
          columns={columns}
        />
      );
    }

    if (tableName == "audios") {
      return (
        <AudioListTableUI
          {...rest}
          getTableProps={getTableProps}
          getRowProps={getRowProps}
          headerGroups={headerGroups}
          page={page}
          isLoading={isLoading}
          hiddenIsLoading={hiddenIsLoading}
          prepareRow={prepareRow}
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          previousPage={previousPage}
          nextPage={nextPage}
          pageIndex={pageIndex}
          pageCount={controlledPageCount}
          setPageSize={setControlledPageSizeFn}
          pageSize={pageSize}
          filters={filters}
          setFilter={setFilter}
          setMultipleFilters={setMultipleFilters}
          CustomFilters={Filters}
          minRows={minRows}
          columns={columns}
        />
      );
    }

    if (tableName == "paymentHistory") {
      return (
        <PaymentHistoryTableUI
          {...rest}
          getTableProps={getTableProps}
          getRowProps={getRowProps}
          headerGroups={headerGroups}
          page={page}
          isLoading={isLoading}
          hiddenIsLoading={hiddenIsLoading}
          prepareRow={prepareRow}
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          previousPage={previousPage}
          nextPage={nextPage}
          pageIndex={pageIndex}
          pageCount={controlledPageCount}
          setPageSize={setControlledPageSizeFn}
          pageSize={pageSize}
          filters={filters}
          setFilter={setFilter}
          setMultipleFilters={setMultipleFilters}
          CustomFilters={Filters}
          minRows={minRows}
          columns={columns}
        />
      );
    }

    if (tableName == "admins") {
      return (
        <AdminTableUI
          {...rest}
          getTableProps={getTableProps}
          getRowProps={getRowProps}
          headerGroups={headerGroups}
          page={page}
          isLoading={isLoading}
          hiddenIsLoading={hiddenIsLoading}
          prepareRow={prepareRow}
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          previousPage={previousPage}
          nextPage={nextPage}
          pageIndex={pageIndex}
          pageCount={controlledPageCount}
          setPageSize={setControlledPageSizeFn}
          pageSize={pageSize}
          filters={filters}
          setFilter={setFilter}
          setMultipleFilters={setMultipleFilters}
          CustomFilters={Filters}
          minRows={minRows}
          columns={columns}
        />
      );
    }

    if (tableName == "subusers") {
      return (
        <SubuserTableUI
          {...rest}
          getTableProps={getTableProps}
          getRowProps={getRowProps}
          headerGroups={headerGroups}
          page={page}
          isLoading={isLoading}
          hiddenIsLoading={hiddenIsLoading}
          prepareRow={prepareRow}
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          previousPage={previousPage}
          nextPage={nextPage}
          pageIndex={pageIndex}
          pageCount={controlledPageCount}
          setPageSize={setControlledPageSizeFn}
          pageSize={pageSize}
          filters={filters}
          setFilter={setFilter}
          setMultipleFilters={setMultipleFilters}
          CustomFilters={Filters}
          minRows={minRows}
          columns={columns}
        />
      );
    }

    return (
      <TableUI
        {...rest}
        getTableProps={getTableProps}
        getRowProps={getRowProps}
        headerGroups={headerGroups}
        page={page}
        isLoading={isLoading}
        hiddenIsLoading={hiddenIsLoading}
        prepareRow={prepareRow}
        gotoPage={gotoPage}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        previousPage={previousPage}
        nextPage={nextPage}
        pageIndex={pageIndex}
        pageCount={controlledPageCount}
        setPageSize={setControlledPageSizeFn}
        pageSize={pageSize}
        filters={filters}
        setFilter={setFilter}
        setMultipleFilters={setMultipleFilters}
        CustomFilters={Filters}
        minRows={minRows}
        columns={columns}
      />
    );
  }
);
